import { NoSsr } from "@mui/base";
import { useAsync } from "@react-hookz/web";
import last from "lodash/last";
import { useRouter } from "next/router";
import type { FunctionComponent, ReactNode } from "react";
import { useEffect, useMemo } from "react";
import type { NewsletterWidgetProps } from "scmp-sdk";
import { NewsletterWidget, useNewsletterApi } from "scmp-sdk";
import type { NewsletterItem } from "scmp-sdk/components/newsletter/types";

import { notEmpty } from "shared/lib/utils";

export type Props = NewsletterWidgetProps & {
  children?: ReactNode;
  className?: string;
  newsletterId?: string;
  newsletterItems?: NewsletterItem[];
  packageDisplayName?: string;
  packageKey?: string;
  useOnlyPackageNewsletters?: boolean;
};

export const NewsletterWidgetContainer: FunctionComponent<Props> = ({
  className,
  newsletterId,
  newsletterItems = [],
  packageDisplayName,
  packageKey,
  useOnlyPackageNewsletters = false,
  ...newsletterProps
}) => {
  const { fetchNewsletterByIds } = useNewsletterApi();
  const { query } = useRouter();

  const utmNewsletterId = useMemo(() => {
    if (!query.utm_content) return;
    const utmContents = Array.isArray(query.utm_content)
      ? query.utm_content
      : query.utm_content.split("_");
    if (utmContents.length <= 1) return;
    return last(utmContents);
  }, [query.utm_content]);

  const needToFetchNewsletterIds = useMemo(
    () => [utmNewsletterId, newsletterId].filter(notEmpty),
    [newsletterId, utmNewsletterId],
  );

  const [{ result: fetchedNewsletterItems = [] }, { execute }] = useAsync(async () => {
    if (needToFetchNewsletterIds.length === 0) return [] as NewsletterItem[];
    return fetchNewsletterByIds(needToFetchNewsletterIds, needToFetchNewsletterIds.length);
  });

  useEffect(() => void execute(), [execute, needToFetchNewsletterIds]);

  // MergedNewsletter = package newsletters + utm newsletter (if exist) + Newsletters in props + newsletter id from props
  const mergedNewsletters = useMemo(() => {
    const utmNewsletter = utmNewsletterId
      ? fetchedNewsletterItems.find(newsletter => newsletter.entityId === utmNewsletterId)
      : undefined;

    const newsletterFromProps = newsletterId
      ? fetchedNewsletterItems.find(newsletter => newsletter.entityId === newsletterId)
      : undefined;

    const mergedNewsletters = [...newsletterItems, newsletterFromProps].filter(notEmpty);

    if (useOnlyPackageNewsletters) {
      return newsletterItems;
    }

    return utmNewsletter ? [utmNewsletter, ...mergedNewsletters] : mergedNewsletters;
  }, [
    fetchedNewsletterItems,
    newsletterId,
    newsletterItems,
    utmNewsletterId,
    useOnlyPackageNewsletters,
  ]);

  return (
    <NoSsr>
      <NewsletterWidget
        {...newsletterProps}
        className={className}
        newsletterItems={mergedNewsletters}
        packageDisplayName={packageDisplayName}
        packageKey={packageKey}
      />
    </NoSsr>
  );
};

NewsletterWidgetContainer.displayName = "NewsletterWidgetContainer";
